import { throttle } from 'throttle-debounce';
import { fetchIcon } from '../components/atoms/atomIcon';

/**
 * Set custom property value
 * @param key Key
 * @param value Value
 * @param element Target element
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/Using_CSS_custom_properties}
 * @example
 *   ```js
 *   setCssPropertyValue('--foo', 'bar');
 *   ```
 *
 *   ```css
 *   :root {
 *     --foo: 'bar'
 *   }
 *   ```
 *
 *   ```js
 *   setCssPropertyValue('--foo', 'bar', document.querySelector('.foo'));
 *   ```
 *   ```css
 *   .foo {
 *     --foo: 'bar'
 *   }
 *   ```
 */
export const setCssPropertyValue = (
  key: string,
  value: string,
  element: HTMLElement = document.documentElement
) => {
  element.style.setProperty(key, value);
};

/**
 * Get custom property value
 * @param key Property name like `'--my-variable'`
 * @param element Any target element
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/Using_CSS_custom_properties}
 * @example
 *   ```css
 *   :root {
 *     --foo: 'bar'
 *   }
 *   ```
 *
 *   ```js
 *   getCssPropertyValue('--foo'); // 'bar'
 *   ```
 *
 *   ```css
 *   .foo {
 *     --foo: 'bar'
 *   }
 *   ```
 *
 *   ```js
 *   getCssPropertyValue('--foo', document.querySelector('.foo')); // 'bar'
 *   ```
 */
export const getCssPropertyValue = (
  key: string,
  element: HTMLElement = document.body
) => window.getComputedStyle(element).getPropertyValue(key).trim();

/**
 * Returns a random number between min (inclusive) and max (inclusive)
 * @param min Minimum value
 * @param max Maximum value
 * @returns Value between {@link min} and {@link max}
 * @example
 *   getRandomNumberInRange(1, 3); // 1.28
 *   getRandomNumberInRange(1, 3); // 2.93
 */
export const getRandomNumberInRange = (min: number, max: number): number => {
  // Generate a random decimal between 0 and 1
  const randomDecimal = Math.random();

  // Scale the random decimal to the desired range
  const scaledRandom = randomDecimal * (max - min) + min;

  // Round the scaled random value to 2 decimal places
  const roundedRandom = Math.round(scaledRandom * 100) / 100;

  // Return the final result
  return roundedRandom;
};

/**
 * Find YouTube Video ID
 * @param url Any YouTube url
 * @returns YouTube Video ID
 */
export const YouTubeIdFromUrl = (url: string) => {
  if (!url) return;

  let parts = url.split('v=');
  if (!parts || parts.length < 2) {
    parts = url.split('.be/');
  }
  if (!parts || parts.length < 2) return;

  let id = parts[1];
  if (!id) return;

  const ampersandPosition = id.indexOf('&');
  if (ampersandPosition !== -1) {
    id = id.substring(0, ampersandPosition);
  }

  return id;
};

/**
 * Create YouTube iframe from a url
 * @param {string} url YouTube url
 * @returns {HTMLIFrameElement} YouTube iframe
 */
export const YouTubeIframeFromUrl = (url) => {
  const id = YouTubeIdFromUrl(url);

  if (id) {
    const el = document.createElement('iframe');
    el.dataset.src = `https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1`;
    el.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture';
    return el;
  }
};

/**
 * Find Vimeo Video ID
 * @param {string} src Any Vimeo url
 * @returns {string | undefined} Vimeo Video ID
 */
export const VimeoIdFromUrl = (src) => {
  if (!src) return;
  return src.match(
    /(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|)(\d+)(?:|\/\?)/
  )[4];
};

/**
 * Create Vimeo iframe from a url
 * @param {string} url Vimeo url
 * @returns {HTMLIFrameElement} Vimeo iframe
 */
export const VimeoIframeFromUrl = (url) => {
  const id = VimeoIdFromUrl(url);

  if (id) {
    const el = document.createElement('iframe');
    el.dataset.src = `https://player.vimeo.com/video/${id}?dnt=1&portrait=0&autoplay=true&title=0&byline=0`;
    // el.src = el.src + '&muted=1'
    el.allow = 'autoplay; fullscreen; picture-in-picture';
    return el;
  }
};

/*
 * Trap focus on element. This can improve the accessibility with Popups/overlays (A11Y.
 * @param el Any HTML Element
 * @see {@link https://gomakethings.com/how-to-get-the-first-and-last-focusable-elements-in-the-dom/}
 * @returns Function that removes all events listeners that were added by this function
 * @example
 *   const removeTrapFocus = trapFocus(document.createElement('div')) // focus is trapped
 *   removeTrapFocus() // focus is no longer trapped
 */
export const trapFocus = (el: HTMLElement) => {
  /** Elements in {@link el} that can be focussed */
  const elements: HTMLElement[] = Array.from(
    el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  );

  /** The first focussable element in {@link el} */
  let first: HTMLElement | null = null;

  /** The last focussable element in {@link el} */
  let last: HTMLElement | null = null;

  if (elements && elements.length) {
    first = elements[0];
    last = elements[elements.length - 1];
  }

  /**
   * On keydown on {@link first}
   * @param e Keyboard event
   */
  const onKeyDownFirst = (e: KeyboardEvent) => {
    if (e.code === 'Tab' && e.shiftKey) {
      e.preventDefault();
      setTimeout(() => {
        last?.focus();
      }, 100);
    }
  };

  /**
   * On keydown on {@link last}
   * @param e Keyboard event
   */
  const onKeyDownLast = (e: KeyboardEvent) => {
    if (e.code === 'Tab') {
      if (!e.shiftKey) {
        e.preventDefault();
        setTimeout(() => {
          first?.focus();
        }, 100);
      }
    }
  };

  if (first || last) {
    first?.addEventListener('keydown', onKeyDownFirst);
    last?.addEventListener('keydown', onKeyDownLast);
  }

  /**
   * Remove all event listeners
   */
  const cleanup = () => {
    first?.removeEventListener('keydown', onKeyDownFirst);
    last?.removeEventListener('keydown', onKeyDownLast);
  };

  return cleanup;
};

/**
 * Set CSS root variable value
 * @param key Variable name
 * @param key Variable balue
 * @example
 *   setCSSRootValue('--foo', 'bar');
 *
 *   :root {
 *     --foo: 'bar'
 *   }
 */
export const setCSSRootValue = (key: string, value: string) => {
  return document.body.style.setProperty(key, value);
};

/**
 * Detect click outside of an element
 * @param el Any element
 * @param callback Callback function
 * @returns Function that removes all events listeners that were added by this function
 * @example
 *   const removeOnClickOutside = onClickOutside(document.getElementById('foo'), (e: MouseEvent) => {
 *     console.log('you clicked outside of the element', e.currentTarget, e.target)
 *   }) // now listening
 *   removeOnClickOutside() // not listening anymore
 */
export const onClickOutside = (
  el: HTMLElement,
  callback?: (e: MouseEvent) => void
) => {
  /**
   * Clicking on the {@link el} or any child will do nothing
   * @param e Click event
   */
  const clickInside = (e: MouseEvent) => e.stopPropagation();

  /**
   * Clicking outside of the {@link el} will call the callback function and remove the event listeners attached
   */
  const clickOutside = (e: MouseEvent) => {
    if (callback) callback.call(this, e);
    el.removeEventListener('mouseup', clickInside);
  };

  document.addEventListener('mouseup', clickOutside.bind(this), { once: true });
  el.addEventListener('mouseup', clickInside);

  /**
   * Remove all event listeners
   */
  const cleanup = () => el.removeEventListener('mouseup', clickInside);

  return cleanup;
};

/**
 * `getBoundingClientRect` without transforms. This allows you to use `transform: translateY(10px)` on an element – but get the original `DOMRect` without the transformed position. Does not work with `skew` or `rotate`.
 * @see {@link https://stackoverflow.com/a/57876601}
 * @author Adam Leggett
 * @param el Any element
 */
export const getBoundingClientRectWithoutTransform = (
  el: HTMLElement
): DOMRectReadOnly => {
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  const tx = style.transform;

  let ta: string[];

  if (tx) {
    let sx: number;
    let sy: number;
    let dx: number;
    let dy: number;

    if (tx.startsWith('matrix3d(')) {
      ta = tx.slice(9, -1).split(/, /);
      sx = +ta[0];
      sy = +ta[5];
      dx = +ta[12];
      dy = +ta[13];
    } else if (tx.startsWith('matrix(')) {
      ta = tx.slice(7, -1).split(/, /);
      sx = +ta[0];
      sy = +ta[3];
      dx = +ta[4];
      dy = +ta[5];
    } else {
      return rect;
    }

    const to = style.transformOrigin;
    const x = rect.x - dx - (1 - sx) * parseFloat(to);
    const y =
      rect.y - dy - (1 - sy) * parseFloat(to.slice(to.indexOf(' ') + 1));
    const w = sx ? rect.width / sx : el.offsetWidth;
    const h = sy ? rect.height / sy : el.offsetHeight;

    const rectWithoutTransform: DOMRectReadOnly = {
      x,
      y,
      width: w,
      height: h,
      top: y,
      right: x + w,
      bottom: y + h,
      left: x,
      toJSON: () => {
        return;
      }
    };
    return rectWithoutTransform;
  } else {
    return rect;
  }
};

/**
 * Wrap all occurrences of a {@link keyword} in an {@link element} with `<mark>` element
 * @param element Any element containing only text
 * @param keyword The search keyword (can be a single word or a phrase)
 */
export function wrapTextWithSpan(element: HTMLElement, keyword: string): void {
  // Helper function to escape special characters in a regular expression
  function escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const text = element.textContent?.trim() || '';

  if (!keyword || !text) {
    return;
  }

  // Use a regular expression to match the keyword with word boundaries
  const regex = new RegExp(
    `\\b${escapeRegExp(keyword)}\\b|${escapeRegExp(keyword)}`,
    'gi'
  );

  const output = text.replace(regex, `<mark>$&</mark>`);
  element.innerHTML = output;
}

/**
 * Set cookie
 * @param name Name
 * @param value Value
 * @param days Days this cookie will be stored
 * @param path Cookie path (default is `'/'`)
 *
 * @example
 * setCookie('myCookie', 'someValue', 7); // Set a cookie named 'myCookie' with the value 'someValue' that will expire in 7 days
 */
export const setCookie = (
  name: string,
  value?: string | number | object,
  days?: number,
  path = '/'
): void => {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `;expires=${date.toUTCString()}`;
  }

  let cookieValue = '';

  if (typeof value === 'object') {
    cookieValue = JSON.stringify(value);
  } else {
    cookieValue = value ? value.toString() : '';
  }

  // set cookie
  document.cookie = `${name}=${cookieValue}${expires};path=${path}`;
};

/**
 * Get cookie value
 * @param name Cookie name
 * @returns Cookie value
 * @example
 *   // Suppose the user has a cookie named 'token' with the value 'abcdef123456'
 *   const token = getCookie('token');
 *   console.log(token); // Output: 'abcdef123456'
 *   // Suppose the user has a cookie named 'age' with the value '22'
 *   const age = getCookie('age', true);
 *   console.log(age); // Output: 22
 */
export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue;
  } else {
    return null;
  }
};

/**
 * Generate hash from any string.
 * @param s Any string
 * @returns Hash value like `"1805474036"`
 */
export const generateHashFromString = (s: string) => {
  return s
    .split('')
    .reduce(function (a, b) {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0)
    .toString();
};

/**
 * Convert a CSS time unit like `0.25s` to a `number` representing milliseconds.
 * @param duration Any CSS time unit like `0.25s` or `250ms`
 * @returns Time in milliseconds like `250`
 * @example
 *   convertCssTimeToMilliseconds('0.25s'); // `250`
 *   convertCssTimeToMilliseconds('250ms'); // `250`
 */
export const convertCssTimeToMilliseconds = (duration: string): number =>
  parseFloat(duration) * (/\ds$/.test(duration) ? 1000 : 1);

/**
 * Returns the element height including margins
 * @param el Any element
 * @returns Size in px
 */
export const outerHeight = (el: HTMLElement): number => {
  const height = el.offsetHeight;
  const style = window.getComputedStyle(el);

  return ['top', 'bottom']
    .map((side) => parseInt(style[`margin-${side}`]))
    .reduce((total, side) => total + side, height);
};

/**
 * In this component we use two WYSIWYG editors, a main content block and a sidebar, that are horizontally aligned (left, right). If the sidebar's first headline contains a kicker element, the main content block should always be aligned with the upper edge of the sidebar's first headline.
 * @param contentContainer Element to be aligned
 * @param potentialKickerContainer Element to look for kicker elements
 * @param breakpoint Breakpoint from which layout is horizontal
 * @requires {@link outerHeight}
 */
export const keepContentAlignedBelowKicker = (
  contentContainer: HTMLElement,
  potentialKickerContainer: HTMLElement,
  breakpoint: number
) => {
  if (!contentContainer || !potentialKickerContainer || !breakpoint) return;

  /** Look for kicker elements */
  const kicker = <HTMLElement>potentialKickerContainer.querySelector('.kicker');
  if (!kicker) return;

  const onResize = throttle(100, () => {
    if (window.innerWidth >= breakpoint) {
      /** We will add a paddingTop to {@link contentContainer} */
      const spacing = outerHeight(kicker);

      /** Add {@link spacing}  */
      if (spacing > 0) {
        contentContainer.style.paddingTop = `${spacing}px`;
      }
    } else {
      contentContainer.style.paddingTop = '';
    }
  });

  window.addEventListener('resize', onResize);

  onResize();
};

/** RGB Color */
interface RgbColor {
  /** Red (from `0` to `255`) */
  r: number;

  /** Green (from `0` to `255`) */
  g: number;

  /** Blue (from `0` to `255`) */
  b: number;
}

/**
 * Convert a hex value to RGB
 * @requires {@link RgbColor}
 * @param hex Any HEX value like `'#ffffff'` with a `length` of `7` characters (not `'#fff'`)
 * @returns RGB Color if successfull
 * @example
 *   convertHexToRgb('#ffffff') // {r: 255, g: 255, b: 255}
 *   convertHexToRgb('#000000') // {r: 0, g: 0, b: 0}
 */
const convertHexToRgb = (hex: string) => {
  if (hex.length !== 7)
    throw Error(`Argument 'hex' must be of length '7' but was '${hex.length}'`);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result || (result && (!result[1] || !result[2] || !result[3])))
    return undefined;

  const color: RgbColor = {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  };

  return color;
};

/**
 * Find out if two colors have a high enough contrast, based on the WCAG recommendations. Produces the same results as https://userway.org/contrast/?fg=3e8438&bg=ffffff
 * @requires {@link convertHexToRgb}
 * @param color1 Foreground/text color
 * @param color2 Background color
 * @param method Method to compare colors by
 * @returns Result
 * @see {@link https://alvaromontoro.com/blog/67854/building-your-own-color-contrast-checker}
 * @author Wanja Karl
 * @example
 *   contrastChecker('#ffffff', '#000000', 'AA-level large text') // true
 *   contrastChecker('#ffffff', '#cccccc', 'AA-level large text') // false
 */
export const contrastChecker = (
  color1: string,
  color2: string,
  method:
    | 'AA-level large text'
    | 'AA-level small text'
    | 'AAA-level large text'
    | 'AAA-level small text'
): boolean => {
  const color1rgb = convertHexToRgb(color1);
  const color2rgb = convertHexToRgb(color2);

  const luminance = (r, g, b) => {
    const a = [r, g, b].map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  };

  /** Calculate the relative luminance of {@link color1} */
  const color1luminance = luminance(color1rgb.r, color1rgb.g, color1rgb.b);

  /** Calculate the relative luminance of {@link color2} */
  const color2luminance = luminance(color2rgb.r, color2rgb.g, color2rgb.b);

  /** Calculation of the color contrast ratio */
  const ratio =
    color1luminance > color2luminance
      ? (color2luminance + 0.05) / (color1luminance + 0.05)
      : (color1luminance + 0.05) / (color2luminance + 0.05);

  /** Result depending on WCAG requirements */
  let result;

  if (method === 'AA-level large text') {
    result = ratio < 1 / 3;
  } else if (method === 'AA-level small text') {
    result = ratio < 1 / 4.5;
  } else if (method === 'AAA-level large text') {
    result = ratio < 1 / 4.5;
  } else if (method === 'AAA-level small text') {
    result = ratio < 1 / 7;
  } else {
    throw new Error("Parameter 'method' not valid.");
  }

  return result;
};

/**
 * Get elements in columns, sorted
 * @param el Any element that has `columns: n`
 * @returns An array for each column, each containing the items of that column in order
 * @example
 *   ```html
 *   <div class="items">
 *     <div class="1"></div>
 *     <div class="2"></div>
 *     <div class="3"></div>
 *     <div class="4"></div>
 *     <div class="5"></div>
 *     <div class="6"></div>
 *   </div>
 *   ```
 *
 *   ```css
 *   .items {
 *     columns: 3;
 *   }
 *   ```
 *
 *   ```js
 *   getItemsInColumns(document.querySelector('.items')) // [[el1, el2], [el3, el4], [el5, el6]]
 *   ```
 */
export const getItemsInColumns = (el: HTMLElement): HTMLElement[][] => {
  const items = Array.from(el.children).map((x) => <HTMLElement>x);

  const columns = {};
  const columnsArray = [];

  items.forEach((item) => {
    const valueToTest = 'offsetLeft';

    /** items in the same column should have the same value for {@link valueToTest} */
    if (!columns[item[valueToTest]]) {
      columns[item[valueToTest]] = [];
    }

    columns[item[valueToTest]].push(item);
  });

  for (let i = 0; i < Object.keys(columns).length; i++) {
    columnsArray.push([]);
  }

  Object.entries(columns).forEach((x, i) => {
    const values = <HTMLElement[]>x[1];

    values.forEach((n) => {
      columnsArray[i].push(n);
    });
  });

  return columnsArray;
};

/**
 * Options for {@link splitRange}
 */
interface SplitRangeOptions {
  /** The minimum value of the range. */
  min: number;

  /** The maximum value of the range. */
  max: number;

  /** The number of segments to divide the range into (must be greater than 1). */
  splitBy: number;

  /** The step size. Rounds the results to match the `step` attribute to work with `<input type="range" step="100">` */
  stepSize?: number;

  /** The number of decimal places to round each segment to (optional, defaults to `0`). */
  decimalPlaces?: number;
}

/**
 * Splits a numeric range into equal segments based on the specified parameters.
 * @param options - Options
 * @throws {Error} If {@link SplitRangeOptions.splitBy} is less than or equal to `1`.
 * @throws {Error} If {@link SplitRangeOptions.min} > {@link SplitRangeOptions.max}
 * @returns An array containing the segmented values within the specified range.
 * @requires {@link SplitRangeOptions}
 * @example
 *   splitRange(0, 100, 3) // [0, 50, 100]
 *   splitRange(100, 300, 3) // [100, 200, 300]
 *   splitRange(0, 1, 4, 2) // [0, 0.33, 0.67, 1]
 *   splitRange(1, 5, 5, 1) // [1, 1.8, 2.6, 3.4, 4.2, 5]
 *   splitRange({min: 1000, max: 5000, splitBy: 4}) // [0, 2333, 3667, 5000]
 *   splitRange({min: 1000, max: 5000, splitBy: 4, stepSize: 100}) // [0, 2300, 3700, 5000]
 *   splitRange({min: 1000, max: 5000, splitBy: 4, decimalPlaces: 2}) // [0, 2333.33, 3666.77, 5000]
 */
export const splitRange = (options: SplitRangeOptions): number[] => {
  if (options.splitBy <= 1) {
    throw new Error('splitBy must be greater than 1');
  }

  if (options.min > options.max) {
    throw new Error('min must be smaller than max');
  }

  const range: number[] = [];
  const step = (options.max - options.min) / (options.splitBy - 1);

  for (let i = 0; i < options.splitBy; i++) {
    const value = options.min + i * step;

    if (options.stepSize) {
      const roundedToStep =
        Math.round(value / options.stepSize) * options.stepSize;
      range.push(Number(roundedToStep.toFixed(options.decimalPlaces)));
    } else {
      range.push(Number(value.toFixed(options.decimalPlaces || 0)));
    }
  }

  return range;
};

/**
 * Try to set the height of an `HTMLIFrameElement` automatically based on its contents. Will add a class `"staticMinHeight"` on the <iframe> element when it fails.
 * @param iframe Any iframe
 */
export const iframeAutoHeight = (iframe?: HTMLIFrameElement) => {
  if (!iframe) return;
  /** Iframe inner height in pixel. Possibly throws an error when the client blocks this information. */
  let height: number;

  const update = () => {
    try {
      /** This might be blocked by the client CORS policy */
      height =
        iframe.contentWindow &&
        iframe.contentWindow.document &&
        iframe.contentWindow.document.body &&
        iframe.contentWindow.document.body.scrollHeight
          ? iframe.contentWindow.document.body.scrollHeight
          : undefined;
    } catch (error) {
      console.warn(`${error} Could not determine the height for an iframe.`);
    }

    if (typeof height === 'number' && height > 0) {
      iframe.style.minHeight = height + 'px';
      iframe.classList.remove('staticMinHeight');
    } else {
      iframe.style.minHeight = '';
      iframe.classList.add('staticMinHeight');
    }
  };

  // Load

  iframe.addEventListener('load', () => {
    update();

    /** Fix iframes that come with `<iframe scrolling="no">` for no reason at all. */
    if (iframe.getAttribute('scrolling') === 'no')
      iframe.setAttribute('scrolling', 'yes');
  });

  // Resize

  const onResize = throttle(500, () => {
    update();
  });
  window.addEventListener('resize', onResize);

  // After time

  setTimeout(() => {
    update();
  }, 3000);
};

/**
 * Sort items in the nested archive by a {@link type} and {@link direction}
 * @param items Items
 * @param direction Direction
 * @param type Type
 */
export const sortItemsInNestedArchive = (
  items: HTMLElement[],
  direction: 'ascending' | 'descending',
  type: 'date'
) => {
  // Helper to parse date strings and convert them to Date objects
  const parseDate = (dateString: string) => {
    const date = dateString ? new Date(dateString) : new Date(0);
    return isNaN(date.getTime()) ? new Date(0) : date; // Handle invalid dates by returning a minimal date
  };

  // Main sorting function
  items.sort((a, b) => {
    let aValue, bValue;

    // Determine the value type we are comparing (in this case, 'date')
    if (type === 'date') {
      aValue = parseDate(a.dataset.created);
      bValue = parseDate(b.dataset.created);
    }

    // Handle invalid dates (push them to the end)
    if (isNaN(aValue.getTime())) return 1;
    if (isNaN(bValue.getTime())) return -1;

    let returnValue = 0;

    // Perform the comparison based on the direction
    if (direction === 'ascending') {
      returnValue = aValue.getTime() - bValue.getTime(); // Ascending comparison
    } else if (direction === 'descending') {
      returnValue = bValue.getTime() - aValue.getTime(); // Descending comparison
    }

    return returnValue;
  });

  // Reorder the DOM elements based on the sorted array
  items.forEach((item) => item.parentNode?.appendChild(item));
};

/**
 * Get URL parameter value
 * @param name Parameter name
 * @param convertType Whether to try to convert the parameter value to its actual type (default is false)
 * @returns Parameter value
 * @example
 * // Given URL: http://example.com/?name=John&age=30&isAdmin=true
 *
 * // Without convertType parameter
 * const name = getParam('name'); // Returns "John"
 * const age = getParam('age'); // Returns "30"
 * const isAdmin = getParam('isAdmin'); // Returns "true"
 * const missingParam = getParam('missingParam'); // Returns undefined
 *
 * // With convertType parameter set to true
 * const name2 = getParam('name', true); // Returns "John"
 * const age2 = getParam('age', true); // Returns 30
 * const isAdmin2 = getParam('isAdmin', true); // Returns true
 * const missingParam2 = getParam('missingParam', true); // Returns undefined
 */
export const getParam = (
  name: string,
  convertType = false
): string | number | boolean | object | undefined => {
  const urlParams = new URLSearchParams(window.location.search);
  const paramValue = urlParams.get(name);

  if (!paramValue) {
    return undefined;
  }

  if (convertType) {
    // Try to convert parameter value to its actual type
    if (paramValue === 'true') {
      return true;
    } else if (paramValue === 'false') {
      return false;
    } else if (/^\d+$/.test(paramValue)) {
      return Number(paramValue);
    } else {
      try {
        return JSON.parse(paramValue);
      } catch (e) {
        console.error(e);

        // Not a valid JSON string, return as is
        return paramValue;
      }
    }
  }

  return paramValue;
};

/**
 * Search for `mailto:` and `tel:` buttons in the DOM and replace the icons with the correct ones
 */
export const handleMailAndPhoneButtons = async () => {
  /** Target buttons. We only want primary buttons and `mailto:` or `tel:` items */
  const buttons = <HTMLAnchorElement[]>(
    Array.from(
      document.querySelectorAll('a.button.button--primary:has(.iconWrapper)')
    ).filter(
      (x) =>
        x.hasAttribute('href') &&
        (x.getAttribute('href').startsWith('tel:') ||
          x.getAttribute('href').startsWith('mailto:'))
    )
  );
  if (!buttons.length) return;

  /** SVG HTML code of the phone icon */
  let iconPhone;

  /** SVG HTML code of the mail icon */
  let iconMail;

  await fetchIcon('/assets/icons/ico-phone.svg').then((svg) => {
    iconPhone = svg;
  });
  await fetchIcon('/assets/icons/ico-mail.svg').then((svg) => {
    iconMail = svg;
  });

  // replace the icons
  buttons.forEach((button) => {
    const iconWrapper = button.querySelector('.iconWrapper');
    iconWrapper.classList.add('automatically-updated-icon');
    const href = button.getAttribute('href');

    if (href.startsWith('tel:')) {
      // phone
      iconWrapper.innerHTML = iconPhone;
    } else {
      // mail
      iconWrapper.innerHTML = iconMail;
    }

    // move icon from after the text to before the text
    button.prepend(iconWrapper);
  });
};
